import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, TrendingUp, Key, LogOut, UserX, Trash2, UserPlus, ShieldAlert, Calendar, CheckCircle, ClipboardList, UserCheck, X, BarChart3, Calendar as CalendarIcon, Bell, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';
import NotificationBell from '../components/NotificationBell';
import ModalControlAsistencia from '../components/ModalControlAsistencia';
import { formatearFechaCortaCaracas, formatearHoraCaracas } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Componente para mostrar una cita en la lista
function CitaItem({ expediente, operadores, onAsignar, onVerDetalle, yaAsignada = false }) {
  const [operadorSeleccionado, setOperadorSeleccionado] = useState(
    yaAsignada ? expediente.cita?.operador_id : ''
  );
  const [asignando, setAsignando] = useState(false);

  const handleAsignar = async () => {
    if (!operadorSeleccionado) {
      toast.error('Selecciona un operador');
      return;
    }

    setAsignando(true);
    await onAsignar(expediente.id, operadorSeleccionado);
    setAsignando(false);
  };

  return (
    <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700 hover:border-yellow-500/50 transition-colors group">
      <div className="flex items-start justify-between gap-4">
        <div 
          className="flex-1 cursor-pointer"
          onClick={() => onVerDetalle(expediente.id)}
        >
          <div className="flex items-center gap-3 mb-2">
            <p className="font-semibold text-white group-hover:text-yellow-500 transition-colors">
              {expediente.solicitante.nombre_completo}
            </p>
            {yaAsignada && expediente.cita?.operador_nombre && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                {expediente.cita.operador_nombre}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
            <span>📋 {expediente.numero_expediente}</span>
            <span>👤 {expediente.solicitante.cedula}</span>
            {expediente.cita && (
              <>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatearFechaCortaCaracas(expediente.cita.fecha)}
                </span>
                <span>
                  🕐 {formatearHoraCaracas(expediente.cita.fecha)}
                </span>
              </>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 group-hover:text-yellow-500/70 transition-colors">
            Haz clic para ver detalles completos del expediente
          </p>
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <select
            value={operadorSeleccionado}
            onChange={(e) => setOperadorSeleccionado(e.target.value)}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
            disabled={asignando}
          >
            <option value="">Seleccionar operador...</option>
            {operadores.map((op) => (
              <option key={op.operador.id} value={op.operador.id}>
                {op.operador.name} {!op.operador.active && '(Bloqueado)'}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={handleAsignar}
            disabled={asignando || !operadorSeleccionado}
            className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
          >
            {asignando ? 'Asignando...' : yaAsignada ? 'Reasignar' : 'Asignar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PanelGerencial() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [panelData, setPanelData] = useState(null);
  const [operadorSeleccionado, setOperadorSeleccionado] = useState(null);
  const [metricsNotificaciones, setMetricsNotificaciones] = useState(null);
  
  // Citas para asignación
  const [citasSinAsignar, setCitasSinAsignar] = useState([]);
  const [citasAsignadas, setCitasAsignadas] = useState([]);
  const [loadingCitas, setLoadingCitas] = useState(false);
  
  // Búsqueda por cédula
  const [searchCedula, setSearchCedula] = useState('');
  
  // Expedientes activos
  const [expedientesActivos, setExpedientesActivos] = useState([]);
  const [loadingExpedientes, setLoadingExpedientes] = useState(false);
  const [searchCedulaExpedientes, setSearchCedulaExpedientes] = useState('');
  
  // Modals
  const [showResetModal, setShowResetModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExpedientesModal, setShowExpedientesModal] = useState(false);
  const [showAsistenciaModal, setShowAsistenciaModal] = useState(false); // NUEVO
  
  // Form data
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    name: '',
    email: ''
  });
  
  // Loading states
  const [resettingPassword, setResettingPassword] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  useEffect(() => {
    loadPanelData();
    loadCitas();
    loadMetricsNotificaciones();
    loadExpedientesActivos();
  }, []);

  const loadMetricsNotificaciones = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notificaciones/metricas-hoy`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setMetricsNotificaciones(data);
      }
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  const loadExpedientesActivos = async () => {
    setLoadingExpedientes(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/expedientes`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Filtrar solo expedientes activos (no completados, cancelados ni retenidos)
        const activos = data.filter(exp => 
          !['completado', 'cancelado', 'cliente_retenido'].includes(exp.estado)
        );
        setExpedientesActivos(activos);
      }
    } catch (error) {
      console.error('Error loading expedientes:', error);
    } finally {
      setLoadingExpedientes(false);
    }
  };

  // Filtrar expedientes activos por cédula
  const expedientesActivosFiltrados = searchCedulaExpedientes.trim()
    ? expedientesActivos.filter(exp => 
        exp.solicitante?.cedula?.replace(/\D/g, '').includes(searchCedulaExpedientes.replace(/\D/g, ''))
      )
    : expedientesActivos;

  // Filtrar citas por cédula
  const filterCitasByCedula = (citas) => {
    if (!searchCedula.trim()) return citas;
    const searchTerm = searchCedula.toLowerCase().replace(/\D/g, ''); // Solo números
    return citas.filter(exp => 
      exp.solicitante.cedula.replace(/\D/g, '').includes(searchTerm)
    );
  };

  const citasSinAsignarFiltradas = filterCitasByCedula(citasSinAsignar);
  const citasAsignadasFiltradas = filterCitasByCedula(citasAsignadas);

  const loadPanelData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gerencial/panel-operadores`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPanelData(data);
      } else {
        toast.error('Error al cargar panel');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };


  const loadCitas = async () => {
    setLoadingCitas(true);
    try {
      // Cargar citas sin asignar
      const responseSinAsignar = await fetch(`${BACKEND_URL}/api/gerencial/citas-sin-asignar`, {
        credentials: 'include'
      });
      
      if (responseSinAsignar.ok) {
        const data = await responseSinAsignar.json();
        setCitasSinAsignar(data);
      }
      
      // Cargar citas asignadas
      const responseAsignadas = await fetch(`${BACKEND_URL}/api/gerencial/citas-asignadas`, {
        credentials: 'include'
      });
      
      if (responseAsignadas.ok) {
        const data = await responseAsignadas.json();
        setCitasAsignadas(data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar citas');
    } finally {
      setLoadingCitas(false);
    }
  };

  const handleAsignarCita = async (expedienteId, operadorId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gerencial/asignar-cita?expediente_id=${expedienteId}&operador_id=${operadorId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Cita asignada exitosamente');
        loadCitas(); // Recargar citas
        loadPanelData(); // Recargar panel para actualizar métricas
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al asignar cita');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al asignar cita');
    }
  };

  const handleVerDetalle = (expedienteId) => {
    navigate(`/expediente/${expedienteId}`);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      window.location.href = '/';
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleResetPassword = async () => {
    if (!nuevaPassword || nuevaPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setResettingPassword(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/usuarios/${operadorSeleccionado.id}/resetear-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ nueva_password: nuevaPassword })
        }
      );

      if (response.ok) {
        toast.success('Contraseña reseteada exitosamente');
        setShowResetModal(false);
        setNuevaPassword('');
        setOperadorSeleccionado(null);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al resetear contraseña');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al resetear contraseña');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateOperador = async () => {
    // Validaciones
    if (!newUserData.username || !newUserData.password || !newUserData.name || !newUserData.email) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    if (newUserData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setCreatingUser(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/usuarios/crear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newUserData,
          role: 'operador' // Gerencial solo puede crear operadores
        })
      });

      if (response.ok) {
        toast.success('Operador creado exitosamente');
        setShowCreateModal(false);
        setNewUserData({ username: '', password: '', name: '', email: '' });
        loadPanelData(); // Recargar el panel
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al crear operador');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al crear operador');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleToggleActive = async (operadorId, currentStatus) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/usuarios/${operadorId}/toggle-active`,
        {
          method: 'PATCH',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.active ? 'Operador desbloqueado' : 'Operador bloqueado');
        loadPanelData(); // Recargar
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al cambiar estado');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cambiar estado del operador');
    }
  };

  const handleDeleteOperador = async () => {
    setDeletingUser(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/usuarios/${operadorSeleccionado.id}/eliminar`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Operador eliminado exitosamente');
        setShowDeleteModal(false);
        setOperadorSeleccionado(null);
        loadPanelData(); // Recargar
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al eliminar operador');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar operador');
    } finally {
      setDeletingUser(false);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando panel gerencial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_retiro-kompa/artifacts/0m8n4tpu_KOMPA%204.png" 
              alt="Kompa" 
              className="h-16 w-auto"
            />
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
              Panel Gerencial
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            {/* Badge de Notificaciones */}
            <NotificationBell />
            
            <Button
              size="sm"
              onClick={() => setShowCreateModal(true)}
              className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
            >
              <Users className="w-4 h-4 mr-2" />
              Crear Operador
            </Button>
            <Button
              size="sm"
              onClick={() => setShowAsistenciaModal(true)}
              className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Control Asistencia
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/analytics')}
              className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              📊 Indicadores
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/gestor-citas')}
              className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              📅 Gestor Citas
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-gray-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Estadísticas Globales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Operadores</p>
                  <p className="text-3xl font-bold text-white">{panelData?.total_operadores || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Operadores Activos</p>
                  <p className="text-3xl font-bold text-green-400">{panelData?.operadores_activos || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Citas</p>
                  <p className="text-3xl font-bold text-white">
                    {panelData?.operadores?.reduce((sum, op) => sum + op.estadisticas.total_citas, 0) || 0}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Tasa Promedio Retención</p>
                  <p className="text-3xl font-bold text-white">
                    {panelData?.operadores?.length > 0
                      ? (panelData.operadores.reduce((sum, op) => sum + op.estadisticas.tasa_retencion, 0) / panelData.operadores.length).toFixed(1)
                      : 0}%
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI de Eficiencia de Respuesta */}
        {metricsNotificaciones && (
          <Card className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-blue-500/30 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-400" />
                Eficiencia de Respuesta (Hoy)
              </CardTitle>
              <CardDescription className="text-gray-400">
                Métricas de gestión de notificaciones del día actual
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Notificaciones Nuevas</p>
                  <p className="text-3xl font-bold text-blue-400">{metricsNotificaciones.nuevas_hoy}</p>
                  <p className="text-xs text-gray-500 mt-1">Total recibido hoy</p>
                </div>
                
                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Atendidas</p>
                  <p className="text-3xl font-bold text-green-400">{metricsNotificaciones.atendidas_hoy}</p>
                  <p className="text-xs text-gray-500 mt-1">Marcadas como leídas</p>
                </div>
                
                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Tasa de Respuesta</p>
                  <p className="text-3xl font-bold text-purple-400">{metricsNotificaciones.tasa_respuesta}%</p>
                  <p className="text-xs text-gray-500 mt-1">Eficiencia de atención</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sección de Asignación de Citas */}
        <Card className="bg-gray-800/50 border-gray-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-500" />
              Asignación de Citas
            </CardTitle>
            <CardDescription className="text-gray-400">
              Gestiona la asignación de citas a operadores
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Buscador por Cédula */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar por cédula (ej: 12.345.678)..."
                  value={searchCedula}
                  onChange={(e) => setSearchCedula(e.target.value)}
                  className="w-full px-4 py-2 pl-10 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                />
                <svg 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchCedula && (
                  <button
                    onClick={() => setSearchCedula('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {searchCedula && (
                <p className="text-xs text-gray-400 mt-2">
                  Mostrando resultados para: <span className="text-yellow-500">{searchCedula}</span>
                </p>
              )}
            </div>
            
            <Tabs defaultValue="sin-asignar" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-900/50">
                <TabsTrigger value="sin-asignar">
                  Sin Asignar ({citasSinAsignarFiltradas.length})
                </TabsTrigger>
                <TabsTrigger value="asignadas">
                  Asignadas ({citasAsignadasFiltradas.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="sin-asignar" className="mt-4 max-h-[400px] overflow-y-auto pr-1 space-y-3">
                {loadingCitas ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                    <p className="text-gray-400 text-sm">Cargando citas...</p>
                  </div>
                ) : citasSinAsignarFiltradas.length === 0 ? (
                  <div className="text-center py-12 bg-gray-900/30 rounded-lg">
                    {searchCedula ? (
                      <>
                        <p className="text-gray-400">No se encontraron citas sin asignar para la cédula: {searchCedula}</p>
                        <Button
                          onClick={() => setSearchCedula('')}
                          variant="ghost"
                          className="mt-3 text-yellow-500 hover:text-yellow-400"
                        >
                          Limpiar búsqueda
                        </Button>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                        <p className="text-gray-400">Todas las citas están asignadas</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {citasSinAsignarFiltradas.map((expediente) => (
                      <CitaItem
                        key={expediente.id}
                        expediente={expediente}
                        operadores={panelData?.operadores || []}
                        onAsignar={handleAsignarCita}
                        onVerDetalle={handleVerDetalle}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="asignadas" className="mt-4 max-h-[400px] overflow-y-auto pr-1 space-y-3">
                {loadingCitas ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                    <p className="text-gray-400 text-sm">Cargando citas...</p>
                  </div>
                ) : citasAsignadasFiltradas.length === 0 ? (
                  <div className="text-center py-12 bg-gray-900/30 rounded-lg">
                    {searchCedula ? (
                      <>
                        <p className="text-gray-400">No se encontraron citas asignadas para la cédula: {searchCedula}</p>
                        <Button
                          onClick={() => setSearchCedula('')}
                          variant="ghost"
                          className="mt-3 text-yellow-500 hover:text-yellow-400"
                        >
                          Limpiar búsqueda
                        </Button>
                      </>
                    ) : (
                      <p className="text-gray-400">No hay citas asignadas</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {citasAsignadasFiltradas.map((expediente) => (
                      <CitaItem
                        key={expediente.id}
                        expediente={expediente}
                        operadores={panelData?.operadores || []}
                        onAsignar={handleAsignarCita}
                        onVerDetalle={handleVerDetalle}
                        yaAsignada={true}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Tarjeta: Expedientes Activos */}
        <Card className="bg-gray-800/50 border-gray-700 mb-8" data-testid="expedientes-activos-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-yellow-500" />
              Expedientes Activos
            </CardTitle>
            <CardDescription className="text-gray-400">
              Todos los expedientes en proceso (incluye clientes sin cita)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Buscador por Cédula */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar por cédula..."
                  value={searchCedulaExpedientes}
                  onChange={(e) => setSearchCedulaExpedientes(e.target.value)}
                  className="w-full px-4 py-2 pl-10 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                  data-testid="search-expedientes-activos"
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchCedulaExpedientes && (
                  <button
                    onClick={() => setSearchCedulaExpedientes('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Lista con scroll */}
            <div className="max-h-[400px] overflow-y-auto pr-1 space-y-3">
              {loadingExpedientes ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                  <p className="text-gray-400 text-sm">Cargando expedientes...</p>
                </div>
              ) : expedientesActivosFiltrados.length === 0 ? (
                <div className="text-center py-12 bg-gray-900/30 rounded-lg">
                  <p className="text-gray-400">
                    {searchCedulaExpedientes ? `No se encontraron expedientes para: ${searchCedulaExpedientes}` : 'No hay expedientes activos'}
                  </p>
                </div>
              ) : (
                expedientesActivosFiltrados.map((exp) => (
                  <div
                    key={exp.id}
                    onClick={() => navigate(`/expediente/${exp.id}`)}
                    className="bg-gray-900/50 p-4 rounded-lg border border-gray-700 hover:border-yellow-500/50 transition-colors cursor-pointer group"
                    data-testid={`expediente-activo-${exp.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-white group-hover:text-yellow-500 transition-colors">
                          {exp.solicitante?.nombre_completo}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mt-1">
                          <span>{exp.numero_expediente}</span>
                          <span>{exp.solicitante?.cedula}</span>
                          {exp.cita && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatearFechaCortaCaracas(exp.cita.fecha)}
                            </span>
                          )}
                          {!exp.cita && (
                            <span className="text-orange-400 text-xs">Sin cita</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                          {exp.estado?.replace(/_/g, ' ')}
                        </Badge>
                        <svg className="w-5 h-5 text-gray-500 group-hover:text-yellow-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Total: {expedientesActivosFiltrados.length} expediente(s) activo(s)
            </p>
          </CardContent>
        </Card>

        {/* Lista de Operadores */}
        <div className="space-y-6">
          {panelData?.operadores?.map((operadorData) => (
            <Card key={operadorData.operador.id} className="bg-gray-800/50 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-4 cursor-pointer group"
                    onClick={() => {
                      setOperadorSeleccionado(operadorData.operador);
                      setShowExpedientesModal(true);
                    }}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white font-bold text-xl group-hover:ring-4 group-hover:ring-yellow-500/30 transition-all">
                      {operadorData.operador.name.charAt(0)}
                    </div>
                    <div>
                      <CardTitle className="text-white group-hover:text-yellow-500 transition-colors">
                        {operadorData.operador.name}
                      </CardTitle>
                      <CardDescription className="text-gray-400">
                        @{operadorData.operador.username} • {operadorData.operador.email}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {operadorData.operador.active ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Activo</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Bloqueado</Badge>
                    )}
                    <Button
                      size="sm"
                      onClick={() => {
                        setOperadorSeleccionado(operadorData.operador);
                        setShowResetModal(true);
                      }}
                      className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Cambiar Password
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleToggleActive(operadorData.operador.id, operadorData.operador.active)}
                      className={operadorData.operador.active 
                        ? "bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30"
                        : "bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
                      }
                    >
                      <UserX className="w-4 h-4 mr-2" />
                      {operadorData.operador.active ? 'Bloquear' : 'Desbloquear'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setOperadorSeleccionado(operadorData.operador);
                        setShowDeleteModal(true);
                      }}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Estadísticas del Operador */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Total Citas</p>
                    <p className="text-2xl font-bold text-white">{operadorData.estadisticas.total_citas}</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Completadas</p>
                    <p className="text-2xl font-bold text-white">{operadorData.estadisticas.citas_completadas}</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Retenidos</p>
                    <p className="text-2xl font-bold text-green-400">{operadorData.estadisticas.clientes_retenidos}</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Retiros</p>
                    <p className="text-2xl font-bold text-orange-400">{operadorData.estadisticas.retiros_confirmados}</p>
                  </div>
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Tasa Retención</p>
                    <p className="text-2xl font-bold text-purple-400">{operadorData.estadisticas.tasa_retencion}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {/* Modal de Resetear Password */}
      <ConfirmModal
        isOpen={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setNuevaPassword('');
          setOperadorSeleccionado(null);
        }}
        onConfirm={handleResetPassword}
        title={`Resetear Contraseña - ${operadorSeleccionado?.name}`}
        message={
          <div className="space-y-4">
            <p className="text-gray-300">
              Ingresa la nueva contraseña para <strong>{operadorSeleccionado?.name}</strong>:
            </p>
            <input
              type="password"
              value={nuevaPassword}
              onChange={(e) => setNuevaPassword(e.target.value)}
              placeholder="Nueva contraseña (mín. 6 caracteres)"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
              autoFocus
            />
          </div>
        }
        confirmText={resettingPassword ? "Reseteando..." : "Resetear Contraseña"}
        cancelText="Cancelar"
        isDangerous={false}
      />

      {/* Modal de Crear Operador */}
      <ConfirmModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewUserData({ username: '', password: '', name: '', email: '' });
        }}
        onConfirm={handleCreateOperador}
        title="Crear Nuevo Operador"
        message={
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nombre Completo</label>
              <input
                type="text"
                value={newUserData.name}
                onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                placeholder="Ej: Juan Pérez"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                placeholder="juan.perez@kompa.com"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username <span className="text-xs text-gray-400">(mín. 6 letras + 1 número)</span>
              </label>
              <input
                type="text"
                value={newUserData.username}
                onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
                placeholder="juanperez1"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Contraseña</label>
              <input
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-green-500"
              />
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-xs text-blue-300">
                ℹ️ El operador recibirá estos datos de acceso. Asegúrate de comunicarle su username y contraseña.
              </p>
            </div>
          </div>
        }
        confirmText={creatingUser ? "Creando..." : "Crear Operador"}
        cancelText="Cancelar"
        isDangerous={false}
      />

      {/* Modal de Eliminar Operador */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setOperadorSeleccionado(null);
        }}
        onConfirm={handleDeleteOperador}
        title="Eliminar Operador"
        message={
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-red-300 font-semibold mb-2">⚠️ Acción Irreversible</p>
                <p className="text-gray-300 text-sm">
                  Estás a punto de eliminar permanentemente al operador:
                </p>
                <p className="text-white font-semibold mt-2">
                  {operadorSeleccionado?.name}
                </p>
                <p className="text-gray-400 text-sm">
                  @{operadorSeleccionado?.username} • {operadorSeleccionado?.email}
                </p>
              </div>
            </div>
            <p className="text-gray-300 text-sm">
              Se eliminarán todas sus sesiones activas y no podrá volver a acceder al sistema.
            </p>
          </div>
        }
        confirmText={deletingUser ? "Eliminando..." : "Sí, Eliminar Operador"}
        cancelText="Cancelar"
        isDangerous={true}
      />

      {/* Modal de Expedientes del Operador */}
      {showExpedientesModal && operadorSeleccionado && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header del Modal */}
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white font-bold text-xl">
                  {operadorSeleccionado.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{operadorSeleccionado.name}</h3>
                  <p className="text-sm text-gray-400">@{operadorSeleccionado.username}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowExpedientesModal(false);
                  setOperadorSeleccionado(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Contenido del Modal */}
            <div className="p-6 overflow-y-auto flex-1">
              <h4 className="text-lg font-semibold text-white mb-4">Expedientes Asignados</h4>
              
              {/* Buscar expedientes del operador en panelData */}
              {(() => {
                const operadorData = panelData?.operadores?.find(
                  op => op.operador.id === operadorSeleccionado.id
                );
                
                const expedientes = [
                  ...(operadorData?.citas_pendientes || []),
                  ...(operadorData?.citas_recientes || [])
                ];

                if (expedientes.length === 0) {
                  return (
                    <div className="text-center py-12 bg-gray-900/30 rounded-lg">
                      <p className="text-gray-400">No hay expedientes asignados</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {expedientes.map((expediente) => (
                      <div
                        key={expediente.id}
                        onClick={() => {
                          navigate(`/expediente/${expediente.id}`);
                          setShowExpedientesModal(false);
                        }}
                        className="bg-gray-900/50 p-4 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-white group-hover:text-purple-400 transition-colors mb-2">
                              {expediente.solicitante.nombre_completo}
                            </p>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                              <span>📋 {expediente.numero_expediente}</span>
                              <span>👤 {expediente.solicitante.cedula}</span>
                              <span>📧 {expediente.solicitante.email}</span>
                              {expediente.cita && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatearFechaCortaCaracas(expediente.cita.fecha)} {formatearHoraCaracas(expediente.cita.fecha)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                              {expediente.estado.replace(/_/g, ' ')}
                            </Badge>
                            <svg className="w-5 h-5 text-gray-500 group-hover:text-purple-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* NUEVO: Modal de Control de Asistencia */}
      <ModalControlAsistencia
        isOpen={showAsistenciaModal}
        onClose={() => setShowAsistenciaModal(false)}
        onSuccess={() => {
          loadCitas(); // Recargar citas por si hay cambios
        }}
      />
    </div>
  );
}
